/**
 * ----------------------------------------------------------------------------------------------------------------------
 * GALLERY THUMBNAIL RAIL
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * On desktop the thumbnail rail is bounded by the product photo (see the styles in the
 * `product-gallery` snippet), so the thumbnails scroll inside the rail instead of spilling below the
 * image. This element drives the two arrows around the scroller: they only appear when there is
 * something to scroll to, and each one is disabled once that end is reached.
 *
 * The rail is `display: contents` in every other layout, where the thumbnails never overflow
 * vertically — the arrows then simply stay hidden.
 */
(() => {
  const SCROLL_RATIO = 0.85; // how much of the visible rail one arrow click travels

  class GalleryThumbnailRail extends HTMLElement {
    #scroller = null;
    #buttons = [];
    #resizeObserver = null;
    #mutationObserver = null;

    connectedCallback() {
      this.#scroller = this.querySelector('.product-gallery__thumbnail-scroller');
      this.#buttons = Array.from(this.querySelectorAll('[data-rail-direction]'));

      if (!this.#scroller || this.#buttons.length === 0) {
        return;
      }

      this.update = this.update.bind(this);
      this.onArrowClicked = this.onArrowClicked.bind(this);

      this.#scroller.addEventListener('scroll', this.update, { passive: true });
      this.#buttons.forEach((button) => button.addEventListener('click', this.onArrowClicked));

      // Thumbnails come in at their intrinsic height once loaded, and the rail itself is resized by
      // the photo next to it.
      this.addEventListener('load', this.update, true);

      this.#resizeObserver = new ResizeObserver(this.update);
      this.#resizeObserver.observe(this.#scroller);

      // Variant image sets hide thumbnails through the `hidden` attribute, which changes how much
      // there is to scroll without resizing anything.
      this.#mutationObserver = new MutationObserver(this.update);
      this.#mutationObserver.observe(this.#scroller, {
        attributeFilter: ['hidden'],
        subtree: true
      });

      this.update();
    }

    disconnectedCallback() {
      this.#scroller?.removeEventListener('scroll', this.update);
      this.#buttons.forEach((button) => button.removeEventListener('click', this.onArrowClicked));
      this.removeEventListener('load', this.update, true);
      this.#resizeObserver?.disconnect();
      this.#mutationObserver?.disconnect();
    }

    update() {
      const maxScroll = this.#scroller.scrollHeight - this.#scroller.clientHeight;
      const scrollTop = this.#scroller.scrollTop;
      const isScrollable = maxScroll > 1;

      this.#buttons.forEach((button) => {
        const isPrevious = button.dataset.railDirection === 'prev';

        button.hidden = !isScrollable;
        button.disabled = isPrevious ? scrollTop <= 1 : scrollTop >= maxScroll - 1;
      });
    }

    onArrowClicked(event) {
      const distance = Math.max(this.#scroller.clientHeight * SCROLL_RATIO, 1);

      this.#scroller.scrollBy({
        top: event.currentTarget.dataset.railDirection === 'prev' ? -distance : distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }
  }

  if (!window.customElements.get('gallery-thumbnail-rail')) {
    window.customElements.define('gallery-thumbnail-rail', GalleryThumbnailRail);
  }
})();
